import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import find from 'lodash/find';
import { isPlatformBrowser } from '@angular/common';
import { errorHandler, noCircDependencyStringify, WebStorageService } from '@cakeiteasy/web-utils';
import { ICartLine } from '@cakeiteasy/order-process';

export interface IEditingCakeSession {
  productTypeId: number;
  clientId: string;
  orderLineBuilderId?: string;
  currentUrl: string;
  savedAt: number;
  data: any;
}

@Injectable({
  providedIn: 'root'
})
export class EditingCakeSessionService {
  private LOCAL_STORAGE_KEY = 'cie-edit-product-session';

  public sessions: IEditingCakeSession[] = [];
  private EXPIRATION_PERIOD: number = 60 * 60 * 1000; // 1hour

  constructor(@Inject(PLATFORM_ID) _platformId: any,
              private _storage: WebStorageService) {
    if (isPlatformBrowser(_platformId)) {
      errorHandler(() => {
        this._fetchSessionsFromStorage();
      });
    }
  }

  public getActiveSession() {
    return find(this.sessions, (session) => {
      return session.data && session.savedAt > (Date.now() - this.EXPIRATION_PERIOD) && session.currentUrl === location.pathname;
    });
  }

  public getActiveSessionBy(productTypeId: number, orderLineBuilderId: string) {
    return find(this.sessions, (session) => {
      const sessionBuilderId = session.orderLineBuilderId;
      return session.data && session.savedAt > (Date.now() - this.EXPIRATION_PERIOD)
        && session.productTypeId === productTypeId && (!orderLineBuilderId || (orderLineBuilderId === sessionBuilderId))
        && session.currentUrl === location.pathname;
    });
  }

  public updateSession(session: IEditingCakeSession, data: ICartLine) {
    session.data = data;
    this._saveSession(session);

    return session;
  }

  public register(productTypeId: number, orderLineBuilderId: string) {
    let session = {
      productTypeId,
      clientId: orderLineBuilderId,
      orderLineBuilderId,
      currentUrl: location.pathname,
      savedAt: Date.now(),
      data: null
    };

    this._saveSession(session);

    return session;
  }

  public unregisterAll() {
    this.sessions = [];

    errorHandler(() => {
      this._storage.local.remove(this.LOCAL_STORAGE_KEY);
    });
  }

  private _fetchSessionsFromStorage() {
    errorHandler(() => {
      let saved = this._storage.local.getAsJson(this.LOCAL_STORAGE_KEY) || [];

      if (saved.length === undefined) {
        this.sessions = [saved];
      } else {
        this.sessions = saved;
      }
    });
  }

  private _saveSession(mutationSession: IEditingCakeSession) {
    errorHandler(() => {
      let found = false;
      this.sessions = this.sessions.map((s) => {
        const savedBuilderId = s.orderLineBuilderId;
        const mutationBuilderId = mutationSession.orderLineBuilderId;
        if (savedBuilderId === mutationBuilderId && s.productTypeId === mutationSession.productTypeId) {
          found = true;
          return mutationSession;
        }

        return s;
      });

      if (!found) {
        this.sessions.push(mutationSession);
      }

      this._storage.local.saveAsJson(this.LOCAL_STORAGE_KEY, this.sessions.map((s) => JSON.parse(noCircDependencyStringify(s))));
    });
  }
}
